import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
    ConfigurationType,
    ICalendarFeedConfigurationData,
    IFeedsConfiguration,
    IFeedsConfigurationProvider,
    INewsFeedConfigurationData,
    INoticeFeedConfigurationData,
    NewsLanguage,
    NewsStatus,
    Provider,
} from '@bciti/types';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';
import { IBreadcrumbItem } from 'src/app/interfaces/breadcrumb/breadcrumb.interface';
import { ConfigurationService, PatchConfigurationPayload } from 'src/app/services/configuration/configuration.service';

@Component({
    selector: 'app-rss-feeds',
    templateUrl: './rss-feeds.component.html',
    styleUrls: ['./rss-feeds.component.scss'],
})
export class RssFeedsComponent implements OnInit {
    breadcrumb: IBreadcrumbItem[] = [
        {
            link: '/organisations',
            title: 'Organisation',
        },
        {
            link: '',
            title: 'Rss Feeds',
        },
    ];

    newsForm: FormGroup;
    providersForm: FormGroup;
    noticesForm: FormGroup;
    calendarForm: FormGroup;
    orgTag: string;

    rssFeedType = new FormControl('News');
    isLoading = false;
    configExists = false;

    constructor(private fb: FormBuilder, private configurationService: ConfigurationService, private route: ActivatedRoute, private toastService: ToastrService) {}

    ngOnInit(): void {
        this.newsForm = this.fb.group({
            newsLanguageUrlInterpretation: new FormControl(false, []),
            newsWordPressFeed: new FormControl(false, []),
            newsCustomFieldsItem: new FormControl([]),
            defaultNewsStatus: new FormControl(NewsStatus.Published, [Validators.required]),
            defaultNewsLanguage: new FormControl(NewsLanguage.frCa, [Validators.required]),
            endDateDaysGap: new FormControl(null, [Validators.required]),
            classic_organisation_id: new FormControl(null, [Validators.required]),
            newsRssFeedType: new FormControl(''),
        });

        this.providersForm = this.fb.group({
            providers: new FormControl([], []),
        });

        this.noticesForm = this.fb.group({
            noticesCustomFieldsItem: new FormControl(undefined, []),
            defaultNoticeDuration: new FormControl(undefined, []),
            defaultNoticeLanguage: new FormControl(undefined, []),
        });

        this.calendarForm = this.fb.group({
            calendars: new FormControl(undefined, []),
        });

        this.route.params.subscribe((param) => {
            if (param.tag) {
                this.orgTag = param.tag;
                this.isLoading = true;
                this.changeFormState(true);

                this.configurationService
                    .getConfigurationByKey(this.orgTag, 'feeds')
                    .pipe(take(1))
                    .subscribe(
                        (data: IFeedsConfiguration) => {
                            this.providersForm.get('providers').setValue(data.providers);

                            this.configExists = true;
                            this.newsForm.patchValue(data.configuration);
                            this.noticesForm.patchValue({
                                defaultNoticeDuration: data.configuration.defaultNoticeDuration ?? undefined,
                                noticesCustomFieldsItem: data.configuration.noticesCustomFieldsItem ?? [],
                            } as INoticeFeedConfigurationData);

                            this.isLoading = false;
                            this.changeFormState(false);
                        },
                        (err) => {
                            if (err instanceof HttpErrorResponse && err.status !== 404) {
                                this.toastService.error(err.error.message, `${err.error.statusCode}: ${err.error.error}`);
                            }
                            this.changeFormState(false);
                        },
                    );
            } else {
                this.changeFormState(false);
            }
        });
    }

    get pageTitle() {
        return `RSS Feeds : ${this.orgTag}`;
    }

    get selectedProviderType() {
        switch (this.rssFeedType.value) {
            case 'News':
                return Provider.NEWS;
            default:
                return Provider.NOTICES;
        }
    }

    changeFormState(disable: boolean) {
        disable ? this.newsForm.disable() : this.newsForm.enable();
        disable ? this.noticesForm.disable() : this.noticesForm.enable();
    }

    async submit() {
        this.newsForm.markAllAsTouched();
        this.noticesForm.markAllAsTouched();
        this.providersForm.markAllAsTouched();
        if (!this.newsForm.valid || !this.noticesForm.valid || !this.providersForm.valid) {
            return;
        }
        this.isLoading = true;
        if (this.configExists) {
            this.updateFeedConfig();
        } else {
            this.addFeedConfig();
        }
    }

    private addFeedConfig() {
        this.configurationService
            .createConfiguration(this.orgTag, this.buildData())
            .pipe(take(1))
            .subscribe(
                (r) => {
                    this.isLoading = false;
                    this.toastService.success('RSS feed has been added to organisation.');
                },
                (e) => {
                    this.isLoading = false;
                    this.toastService.error(e.error.message, `${e.error.statusCode}: ${e.error.error}`);
                },
            );
    }

    private updateFeedConfig() {
        this.configurationService
            .patchConfigurations(this.orgTag, this.buildData())
            .pipe(take(1))
            .subscribe(
                (r) => {
                    this.isLoading = false;
                    this.toastService.success('RSS feed has been updated.');
                },
                (e) => {
                    this.isLoading = false;
                    this.toastService.error(e.error.message, `${e.error.statusCode}: ${e.error.error}`);
                },
            );
    }

    private buildData(): PatchConfigurationPayload<IFeedsConfiguration> {
        return {
            configuration: {
                key: ConfigurationType.FEEDS,
                data: {
                    configuration: {
                        ...(this.trimNullValues(this.newsForm.value) as INewsFeedConfigurationData),
                        ...(this.trimNullValues(this.noticesForm.value) as INoticeFeedConfigurationData),
                        ...(this.trimNullValues(this.calendarForm.value) as ICalendarFeedConfigurationData),
                    },
                    providers: this.providersForm.get('providers').value as IFeedsConfigurationProvider[],
                },
            },
        };
    }

    private trimNullValues(data: any) {
        const trimmed = {};
        for (const [key, value] of Object.entries(data)) {
            if (value !== null) {
                trimmed[key] = value;
            }
        }
        return trimmed;
    }
}
